import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Anchor, Compass, Skull, Utensils } from 'lucide-react';
import { useSession } from './session-context';
import { DISEASES } from './utils/disease-data';
import { NAVIGATION_DC_OPTIONS } from './utils/navigation-dcs';

interface RulesReferenceProps {
  defaultTab?: string;
  scrollToSection?: string;
}

const RulesReference: React.FC<RulesReferenceProps> = ({
  defaultTab = 'voyage',
  scrollToSection
}) => {
  const { isDM } = useSession();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const containerRef = useRef<HTMLDivElement>(null);
  const userIsDM = isDM();

  // Update active tab when defaultTab prop changes
  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  // Scroll to section when scrollToSection prop changes
  useEffect(() => {
    if (scrollToSection && containerRef.current) {
      // Wait for tab content to render
      setTimeout(() => {
        const element = document.getElementById(scrollToSection);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [scrollToSection, activeTab]);

  return (
    <div ref={containerRef} className="container mx-auto p-4">
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-2xl">Stormwrack Seafaring</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className={`grid mb-6 ${userIsDM ? 'grid-cols-4' : 'grid-cols-3'}`}>
              <TabsTrigger value="voyage" className="flex items-center gap-2">
                <Utensils className="h-4 w-4" />
                The Voyage
              </TabsTrigger>
              <TabsTrigger value="navigation" className="flex items-center gap-2">
                <Compass className="h-4 w-4" />
                Navigation
              </TabsTrigger>
              <TabsTrigger value="ships" className="flex items-center gap-2">
                <Anchor className="h-4 w-4" />
                Ships & Damage
              </TabsTrigger>
              {userIsDM && (
                <TabsTrigger value="dm-tables" className="flex items-center gap-2">
                  <Skull className="h-4 w-4" />
                  DM Tables
                </TabsTrigger>
              )}
            </TabsList>

            {/* The Voyage Tab */}
            <TabsContent value="voyage">
              <div className="space-y-6">
                <section id="the-day">
                  <h3 className="font-bold text-lg mb-3">A Day at Sea</h3>
                  <div className="bg-muted p-4 rounded-md space-y-3">
                    <p className="text-sm">
                      The voyage advances one day at a time. Each day the crew eats,
                      and the navigator takes a bearing. Everything else is played
                      out at the table — this app tracks the bookkeeping that would
                      otherwise get forgotten.
                    </p>
                    <p className="text-sm">
                      The DM advances the day from the Voyage tab. A navigation roll
                      is optional: leave it blank to pass a quiet day of coastal
                      sailing without troubling the state machine.
                    </p>
                  </div>
                </section>

                <section id="crew-size">
                  <h3 className="font-bold text-lg mb-3">Crew Size</h3>
                  <div className="bg-muted p-4 rounded-md space-y-3">
                    <p className="text-sm">
                      Crew size is seeded from the vessel's Stormwrack{' '}
                      <strong>complement</strong> — everyone the ship can carry — and
                      the DM adjusts it as people are lost, left behind, or taken aboard.
                    </p>
                    <p className="text-sm">
                      Each vessel also lists a <strong>watch</strong>: the number of
                      hands needed to actually sail her, plus rowers where the ship
                      has oars. Fall below the watch and the ship should start
                      handling badly, at the DM's discretion.
                    </p>
                  </div>
                </section>

                <section id="provisions">
                  <h3 className="font-bold text-lg mb-3">Provisions</h3>
                  <div className="bg-muted p-4 rounded-md space-y-3">
                    <p className="text-sm">
                      One provision point feeds one crew member for one day. Advancing
                      a day deducts <strong>one point per crew member</strong>.
                    </p>
                    <p className="text-sm">
                      Provisions never go negative. If there isn't enough to go round,
                      the panel reports how many of the crew went hungry and leaves
                      the consequences to the table — starvation, sickness, or a crew
                      that starts eyeing the officers is a scene, not a subtraction.
                    </p>
                    <p className="text-sm">
                      Resupply with the ± controls, or use <strong>+1 day</strong> to
                      add exactly one more day's rations for the current crew.
                    </p>
                  </div>
                </section>
              </div>
            </TabsContent>

            {/* Navigation Tab */}
            <TabsContent value="navigation">
              <div className="space-y-6">
                <section id="course-setting">
                  <h3 className="font-bold text-lg mb-3">Setting a Course</h3>
                  <div className="bg-muted p-4 rounded-md space-y-3">
                    <p className="text-sm">
                      Before departure the navigator plots a course. The DC is built
                      from how far the destination is and how well it's known, then
                      modified by how well the starting point is known and by the
                      quality of the charts aboard.
                    </p>
                    <p className="text-sm">
                      <strong>Fail the course-setting check and the ship sets out
                      already off track</strong> — and nobody aboard knows it. That is
                      the whole point of the system: being lost is something the crew
                      discovers, not something they're told.
                    </p>
                  </div>
                </section>

                <section id="staying-on-course">
                  <h3 className="font-bold text-lg mb-3">Staying on Course</h3>
                  <div className="bg-muted p-4 rounded-md space-y-3">
                    <p className="text-sm">
                      One Knowledge (geography) check per day, against a DC set by
                      what's in sight and what the weather is doing:
                    </p>
                    <table className="w-full text-sm mt-2">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Conditions</th>
                          <th className="text-right p-2">DC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {NAVIGATION_DC_OPTIONS.map((option) => (
                          <tr key={option.label} className="border-b">
                            <td className="p-2">{option.label}</td>
                            <td className="p-2 text-right font-semibold">{option.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-sm">
                      <strong>Two failures in a row</strong> and the ship has drifted
                      off course — recorded silently, because the crew doesn't notice
                      yet.
                    </p>
                  </div>
                </section>

                <section id="being-lost">
                  <h3 className="font-bold text-lg mb-3">Realizing You're Lost</h3>
                  <div className="bg-muted p-4 rounded-md space-y-3">
                    <p className="text-sm">
                      Once off course, the ship drifts a random direction each day and
                      the same daily roll becomes a <strong>recognition check</strong>{' '}
                      at DC 20, reduced by 1 for each day of random travel. The longer
                      they wander, the more obvious it becomes.
                    </p>
                    <p className="text-sm">
                      When they recognize it, the roll becomes a{' '}
                      <strong>re-charting attempt</strong> — the course-setting DC
                      again, but now with a completely unknown starting point. Succeed
                      and they're back on course; fail and they drift on.
                    </p>
                    <p className="text-sm">
                      Landfall ends it: the DM marks arrival from the Dead-Reckoning
                      Log and the ship's believed position snaps back to the truth.
                    </p>
                  </div>
                </section>
              </div>
            </TabsContent>

            {/* Ships & Damage Tab */}
            <TabsContent value="ships">
              <div className="space-y-6">
                <section id="hull-sections">
                  <h3 className="font-bold text-lg mb-3">Hull & Rigging Sections</h3>
                  <div className="bg-muted p-4 rounded-md space-y-3">
                    <p className="text-sm">
                      Ships are divided into hull sections spread across decks, plus
                      rigging sections. Each section has its own hit points and
                      hardness. Track damage per section on the Ship Combat tab.
                    </p>
                    <p className="text-sm">
                      Each vessel lists a <strong>sink threshold</strong> — the number
                      of destroyed hull sections at which she goes down.
                    </p>
                  </div>
                </section>

                <section id="fleets">
                  <h3 className="font-bold text-lg mb-3">Fleets</h3>
                  <div className="bg-muted p-4 rounded-md space-y-3">
                    <p className="text-sm">
                      Player and DM vessels are tracked separately. The DM's fleet is
                      hidden from players, so enemy damage can be tracked in the open
                      without giving the game away.
                    </p>
                  </div>
                </section>
              </div>
            </TabsContent>

            {/* DM Tables Tab - Only visible to DM */}
            {userIsDM && (
              <TabsContent value="dm-tables">
                <div className="space-y-6">
                  <section id="disease-table">
                    <h3 className="font-bold text-lg mb-3">Disease Table</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Shipboard diseases for filthy holds, bad water, and coral cuts.
                      For a random result, roll with a weighted distribution: 60%
                      Common, 30% Uncommon, 10% Rare.
                    </p>
                    <Accordion type="single" collapsible className="w-full">
                      {DISEASES.map((disease, index) => (
                        <AccordionItem key={disease.name} value={`disease-${index}`}>
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-3 text-left">
                              <span className="font-semibold">{disease.name}</span>
                              <Badge
                                variant="outline"
                                className={`text-xs ${disease.rarity === 'Common'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : disease.rarity === 'Uncommon'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                  }`}
                              >
                                {disease.rarity}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {disease.contraction} • DC {disease.dc}
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3 text-sm pl-4">
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <strong>Incubation:</strong> {disease.incubation}
                                </div>
                                <div>
                                  <strong>Damage:</strong> {disease.damage}
                                </div>
                                <div>
                                  <strong>Contraction:</strong> {disease.contraction}
                                </div>
                              </div>
                              <div>
                                <strong>Symptoms:</strong>
                                <p className="text-muted-foreground mt-1">{disease.symptoms}</p>
                              </div>
                              {disease.special && (
                                <div className="p-3 bg-red-50 dark:bg-red-950 rounded-md border border-red-200 dark:border-red-800">
                                  <strong className="text-red-700 dark:text-red-300">Special:</strong>
                                  <p className="text-red-600 dark:text-red-400 mt-1">{disease.special}</p>
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </section>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default RulesReference;
